import type { ReactNode } from 'react';
import { Box, alpha } from '@mui/material';

export interface SectionNavItem {
  /** Stable id — matched against `active` and passed to `onChange`. */
  id: string;
  /** Row label (already translated). */
  label: string;
  /** Optional leading glyph (an MUI icon element). */
  icon?: ReactNode;
  /** Optional trailing count. */
  badge?: number;
}

export interface SectionNavProps {
  items: SectionNavItem[];
  /** The active item id. */
  active: string;
  onChange: (id: string) => void;
  /** Optional uppercase section title above the list. */
  title?: string;
  /** Accessible name for the nav landmark. */
  ariaLabel?: string;
}

/**
 * Vertical in-page navigation (entity-detail tabs / settings sub-nav / on-page
 * table of contents) — the MUI port of the prototype kit `SectionNav`. The
 * active row gets a 2px orange (`secondary.main`) rail plus a faint tint;
 * inactive rows are muted and tint on hover. Purely a controlled switcher: it
 * emits `onChange(id)` and does not navigate.
 */
export function SectionNav({ items, active, onChange, title, ariaLabel }: SectionNavProps) {
  return (
    <Box
      component="nav"
      aria-label={ariaLabel}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
    >
      {title && (
        <Box
          sx={{
            px: 1.5,
            pt: 0.5,
            pb: 1.25,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {title}
        </Box>
      )}
      {items.map((it) => {
        const on = it.id === active;
        return (
          <Box
            key={it.id}
            component="button"
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => onChange(it.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              width: '100%',
              textAlign: 'left',
              px: 1.5,
              py: 1,
              border: 0,
              borderLeft: '2px solid',
              borderLeftColor: on ? 'secondary.main' : 'transparent',
              borderRadius: '6px',
              cursor: 'pointer',
              bgcolor: on ? (theme) => alpha(theme.palette.info.main, 0.08) : 'transparent',
              color: on ? 'text.primary' : 'text.secondary',
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontSize: 13,
              fontWeight: on ? 600 : 500,
              lineHeight: 1.3,
              transition: 'background 120ms',
              '& .sa-section-nav-icon svg': {
                fontSize: 16,
                color: on ? 'secondary.main' : 'text.secondary',
              },
              '&:hover': {
                bgcolor: on ? (theme) => alpha(theme.palette.info.main, 0.08) : 'action.hover',
              },
            }}
          >
            {it.icon && (
              <Box component="span" className="sa-section-nav-icon" sx={{ display: 'inline-flex' }}>
                {it.icon}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {it.label}
            </Box>
            {it.badge != null && (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  px: 0.75,
                  py: 0.125,
                  borderRadius: '999px',
                  bgcolor: 'action.selected',
                  color: 'text.secondary',
                }}
              >
                {it.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
